import React, { useState, useEffect } from 'react';
import {
    Button, Card, message, Typography, Space, Modal, Tag, Form, Input, Table, Switch, Popconfirm, Alert
} from 'antd';
import {
    ClockCircleOutlined, PlayCircleOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined
} from '@ant-design/icons';
import {
    hourlyRulesService, HourlyRuleDto,
    CreateHourlyRuleRequest, UpdateHourlyRuleRequest,
    RunHourlyRuleResponse
} from '../services/hourlyRulesService';

const { Title, Text } = Typography;
const { TextArea } = Input;

type HourlyFormState = {
    open: boolean;
    isEdit: boolean;
    editingId: number | null;
    values: {
        ruleName?: string;
        sqlQuery?: string;
        runTimes?: string;
        active?: boolean;
    };
};

const HourlyRulesTab: React.FC = () => {
    const [rules, setRules] = useState<HourlyRuleDto[]>([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [runningId, setRunningId] = useState<number | null>(null);

    const [formModal, setFormModal] = useState<HourlyFormState>({
        open: false,
        isEdit: false,
        editingId: null,
        values: { active: true }
    });
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (rules.length === 0) loadRules();
    }, []);

    const loadRules = async () => {
        setRulesLoading(true);
        try {
            const list = await hourlyRulesService.getAll();
            setRules(list);
        } catch (error: any) {
            console.error('Load hourly rules error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Không thể tải danh sách rule theo giờ';
            message.error(msg);
        } finally {
            setRulesLoading(false);
        }
    };

    const openCreateRule = () => {
        setFormModal({
            open: true,
            isEdit: false,
            editingId: null,
            values: { ruleName: '', sqlQuery: '', runTimes: '', active: true }
        });
        form.setFieldsValue({ ruleName: '', sqlQuery: '', runTimes: '', active: true });
    };

    const openEditRule = (r: HourlyRuleDto) => {
        setFormModal({
            open: true,
            isEdit: true,
            editingId: r.id,
            values: {
                ruleName: r.ruleName,
                sqlQuery: r.sqlQuery,
                runTimes: r.runTimes,
                active: (r.active ?? 0) === 1
            }
        });
        form.setFieldsValue({
            ruleName: r.ruleName,
            sqlQuery: r.sqlQuery,
            runTimes: r.runTimes,
            active: (r.active ?? 0) === 1
        });
    };

    const closeFormModal = () => {
        setFormModal({ open: false, isEdit: false, editingId: null, values: { active: true } });
        form.resetFields();
    };

    const submitFormRule = async () => {
        try {
            const values = await form.validateFields();
            setFormSubmitting(true);

            if (formModal.isEdit && formModal.editingId != null) {
                const payload: UpdateHourlyRuleRequest = {
                    ruleName: String(values.ruleName ?? '').trim(),
                    sqlQuery: String(values.sqlQuery ?? '').trim(),
                    runTimes: String(values.runTimes ?? '').replace(/\s+/g, ''),
                    active: values.active ? 1 : 0
                };
                const res = await hourlyRulesService.update(formModal.editingId, payload);
                message.success(res.message || 'Cập nhật rule thành công');
            } else {
                const payload: CreateHourlyRuleRequest = {
                    ruleName: String(values.ruleName ?? '').trim(),
                    sqlQuery: String(values.sqlQuery ?? '').trim(),
                    runTimes: String(values.runTimes ?? '').replace(/\s+/g, ''),
                    active: values.active ? 1 : 0
                };
                const res = await hourlyRulesService.create(payload);
                message.success(res.message || `Tạo rule thành công (id=${res.id})`);
            }
            closeFormModal();
            await loadRules();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            console.error('Submit hourly rule form error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Lưu rule thất bại';
            message.error(msg);
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleDeleteRule = async (r: HourlyRuleDto) => {
        try {
            const res = await hourlyRulesService.remove(r.id);
            message.success(res.message || 'Xóa rule thành công');
            setRules(prev => prev.filter(x => x.id !== r.id));
        } catch (error: any) {
            console.error('Delete hourly rule error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Xóa rule thất bại';
            message.error(msg);
        }
    };

    const handleToggleActive = async (r: HourlyRuleDto, checked: boolean) => {
        try {
            const res = await hourlyRulesService.toggleActive(r.id, checked);
            message.success(res.message);
            setRules(prev => prev.map(x => x.id === r.id ? { ...x, active: checked ? 1 : 0 } : x));
        } catch (error: any) {
            console.error('Toggle hourly rule active error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Cập nhật trạng thái thất bại';
            message.error(msg);
        }
    };

    const handleRunRule = async (r: HourlyRuleDto) => {
        setRunningId(r.id);
        try {
            const res: RunHourlyRuleResponse = await hourlyRulesService.run(r.id);
            if (res.success) {
                const rows = res.rowsAffected ?? 0;
                message.success(`Chạy rule [${r.ruleName}] thành công: ${rows} dòng ảnh hưởng.`, 5);
                setRules(prev => prev.map(x => x.id === r.id ? { ...x, lastRunAt: new Date().toISOString() } : x));
            } else {
                message.error(res.message || `Chạy rule [${r.ruleName}] thất bại`);
            }
        } catch (error: any) {
            console.groupCollapsed('%c[HourlyRule Run] LỖI — xem chi tiết bên trong:', 'color:#fff;background:#c0392b;padding:2px 8px;border-radius:4px;');
            console.log('error.message =', error?.message);
            console.log('error.response.status =', error?.response?.status);
            const data = error?.response?.data;
            console.log('error.response.data =', data);
            console.error('Stack gốc:', error);
            console.groupEnd();

            const msgBody = data ? (data.message || data.Message) : null;
            const msg = msgBody || error?.message || 'Chạy rule thất bại (lỗi server — mở Console DevTools để xem chi tiết)';
            message.error(msg);
        } finally {
            setRunningId(null);
        }
    };

    const renderRunTimesTag = (runTimes: string) => {
        if (!runTimes) return <Tag>-</Tag>;
        const arr = runTimes.split(/[,;]/).filter(Boolean);
        return (
            <Space size={4} wrap>
                {arr.map((t, i) => (
                    <Tag key={i} color="geekblue" style={{ fontFamily: 'Consolas, monospace' }}>{t.trim()}</Tag>
                ))}
            </Space>
        );
    };

    const ruleColumns: any[] = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            render: (v: any) => <Text code>{v}</Text>
        },
        {
            title: 'Tên Rule',
            dataIndex: 'ruleName',
            key: 'ruleName',
            render: (v: any) => <Text strong>{v}</Text>
        },
        {
            title: 'Mốc giờ chạy',
            dataIndex: 'runTimes',
            key: 'runTimes',
            width: 260,
            render: (v: string, r: HourlyRuleDto) => renderRunTimesTag(r.runTimes)
        },
        {
            title: 'SQL (HIS ReportDB)',
            dataIndex: 'sqlQuery',
            key: 'sqlQuery',
            ellipsis: true,
            render: (v: string) => (
                <Text code type="secondary" style={{ whiteSpace: 'nowrap', maxWidth: 380, display: 'inline-block' }}>
                    {(v || '').replace(/\s+/g, ' ').slice(0, 120)}
                    {(v || '').length > 120 ? ' …' : ''}
                </Text>
            )
        },
        {
            title: 'Lần chạy cuối',
            dataIndex: 'lastRunAt',
            key: 'lastRunAt',
            width: 170,
            render: (v: string | null) => v
                ? <Text type="secondary" style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}>{new Date(v).toLocaleString('vi-VN')}</Text>
                : <Tag color="default">chưa chạy</Tag>
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            width: 90,
            align: 'center' as any,
            render: (_: any, r: HourlyRuleDto) => (
                <Switch
                    checked={(r.active ?? 0) === 1}
                    onChange={(ch) => handleToggleActive(r, ch)}
                    size="small"
                />
            )
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 220,
            render: (_: any, r: HourlyRuleDto) => (
                <Space size={6} wrap>
                    <Button
                        type="primary"
                        size="small"
                        icon={<PlayCircleOutlined />}
                        onClick={() => handleRunRule(r)}
                        loading={runningId === r.id}
                        style={{ backgroundColor: '#1890ff' }}
                    >
                        Chạy
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditRule(r)}
                    >
                        Sửa
                    </Button>
                    <Popconfirm
                        title="Xóa rule này?"
                        description={`Rule "${r.ruleName}" sẽ bị xóa vĩnh viễn.`}
                        okText="Xóa"
                        okType="danger"
                        cancelText="Hủy"
                        onConfirm={() => handleDeleteRule(r)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>Xóa</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <Space direction="vertical" size={4}>
                            <Title level={4} style={{ margin: 0 }}>
                                <ClockCircleOutlined style={{ marginRight: 8, color: '#13c2c2' }} />
                                Danh sách Rules Theo giờ
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Các rule chạy SQL <Text code>FULL QUYỀN</Text> trên CSDL HIS (ReportDB) vào các mốc giờ hàng ngày đã cấu hình. Hệ thống scheduler chạy ngầm mỗi phút check và thực thi tự động, ghi log ra bảng <Text code>system_log</Text>.
                            </Text>
                        </Space>
                        <Space wrap>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadRules}
                                loading={rulesLoading}
                            >
                                Làm mới
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreateRule}
                                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                            >
                                Thêm rule mới
                            </Button>
                        </Space>
                    </Space>

                    <Alert
                        type="error"
                        showIcon
                        message="⚠️ FULL QUYỀN TRUY VẤN HIS (INSERT/UPDATE/DELETE)"
                        description={
                            <div style={{ fontSize: 13 }}>
                                Các rule ở tab này <Text strong style={{ color: '#cf1322' }}>KHÔNG</Text> bị giới hạn read-only.
                                Bạn có thể thực thi mọi lệnh <Text code>INSERT / UPDATE / DELETE / MERGE / EXEC / DROP / ALTER</Text> trực tiếp trên CSDL HIS.
                                <br />
                                <Text strong>Hãy kiểm tra kỹ câu SQL trước khi lưu &amp; kích hoạt rule.</Text>
                                Mọi thay đổi của rule trên HIS đều được ghi log (maloailog = 3) với machamcong = tên người dùng (chạy thủ công) hoặc <Text code>Scheduler</Text> (chạy tự động).
                            </div>
                        }
                    />

                    <Table<HourlyRuleDto>
                        rowKey="id"
                        size="middle"
                        loading={rulesLoading}
                        dataSource={rules}
                        columns={ruleColumns}
                        scroll={{ x: 1200 }}
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `Tổng ${total} rule`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            defaultPageSize: 20
                        }}
                    />
                </Space>
            </Card>

            <Modal
                open={formModal.open}
                title={
                    <Space>
                        {formModal.isEdit ? <EditOutlined /> : <PlusOutlined />}
                        {formModal.isEdit ? 'Chỉnh sửa Rule Theo giờ' : 'Tạo Rule Theo giờ mới'}
                    </Space>
                }
                width={820}
                okText="Lưu"
                cancelText="Hủy"
                confirmLoading={formSubmitting}
                onCancel={closeFormModal}
                onOk={submitFormRule}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ active: true }}
                >
                    <Form.Item
                        label="Tên rule"
                        name="ruleName"
                        rules={[
                            { required: true, message: 'Vui lòng nhập tên rule' },
                            { max: 500, message: 'Tên quá dài (tối đa 500 ký tự)' }
                        ]}
                    >
                        <Input placeholder="VD: Đồng bộ số lượng CLS hàng ngày 12:00 & 18:00" />
                    </Form.Item>

                    <Space style={{ width: '100%' }} align="start" size={16}>
                        <Form.Item
                            label="Mốc giờ chạy (HH:mm, tách bằng dấu phẩy)"
                            name="runTimes"
                            style={{ flex: 1, minWidth: 360 }}
                            rules={[
                                { required: true, message: 'Vui lòng nhập ít nhất 1 mốc giờ' },
                                {
                                    validator: (_, value) => {
                                        const raw = String(value || '').replace(/\s+/g, '');
                                        if (!raw) return Promise.resolve();
                                        const arr = raw.split(/[,;]/).filter(Boolean);
                                        if (arr.length === 0)
                                            return Promise.reject(new Error('Vui lòng nhập ít nhất 1 mốc giờ (HH:mm).'));
                                        const timeRe = /^\d{1,2}:\d{2}$/;
                                        for (const t of arr) {
                                            if (!timeRe.test(t))
                                                return Promise.reject(new Error(`"${t}" không đúng định dạng HH:mm (ví dụ 07:00).`));
                                            const [hh, mm] = t.split(':').map(Number);
                                            if (hh < 0 || hh > 23 || mm < 0 || mm > 59)
                                                return Promise.reject(new Error(`Mốc "${t}" ngoài khoảng thời gian hợp lệ (00:00 → 23:59).`));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            extra={
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Nhập các mốc giờ cách nhau bởi dấu phẩy. Ví dụ: <Text code>07:00,12:00,17:30</Text>
                                </Text>
                            }
                        >
                            <Input
                                placeholder="07:00,12:00,17:00"
                                style={{ fontFamily: 'Consolas, monospace', fontSize: 13 }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Kích hoạt"
                            name="active"
                            valuePropName="checked"
                            style={{ minWidth: 140 }}
                        >
                            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        label={
                            <Space>
                                SQL thực thi trên HIS (ReportDB)
                                <Tag color="red">FULL QUYỀN — INSERT/UPDATE/DELETE/EXEC đều được</Tag>
                            </Space>
                        }
                        name="sqlQuery"
                        rules={[
                            { required: true, message: 'Vui lòng nhập SQL truy vấn' },
                            {
                                validator: (_, value) => {
                                    const v = String(value || '').trim();
                                    if (!v) return Promise.resolve();
                                    return Promise.resolve();
                                }
                            }
                        ]}
                        extra={
                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                <Text type="danger" style={{ fontSize: 12 }}>
                                    ⚠️ Câu này sẽ chạy trực tiếp lên HIS. Hãy chạy thử với nút "Chạy" trước khi bật Auto.
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Mỗi khi chạy (tự động / thủ công), hệ thống sẽ ghi 1 dòng vào <Text code>system_log</Text> (maloailog=3) ghi rõ kết quả thành công / lỗi.
                                </Text>
                            </Space>
                        }
                    >
                        <TextArea
                            rows={10}
                            placeholder={
                                "-- Ví dụ: Cập nhật trạng thái đã gửi thông báo cho BN đăng ký online sau 2h\n" +
                                "UPDATE dangky_online\n" +
                                "SET da_gui_thong_bao = 1, updated_at = GETDATE()\n" +
                                "WHERE trangthai = 1\n" +
                                "  AND created_at < DATEADD(HOUR, -2, GETDATE())\n" +
                                "  AND (da_gui_thong_bao IS NULL OR da_gui_thong_bao = 0);"
                            }
                            style={{ fontFamily: 'Consolas, monospace', fontSize: 13, lineHeight: 1.5 }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default HourlyRulesTab;
